<template>
  <div class="auth-wrapper" v-if="showForm">
    <div class="auth-container">
      <div class="auth-content-container">
        <v-card class="auth-card">
          <h2 class="auth-title">Enter account information</h2>
          <v-form ref="form" validate-on="validate" @submit.prevent="submit">
            <!--
          Crazy fix for autofill Chrome & Safari
          https://bugs.chromium.org/p/chromium/issues/detail?id=468153
          F i x  start
        -->
            <input tabindex="-1" style="opacity: 0; position: absolute" />
            <input tabindex="-1" type="password" style="opacity: 0; position: absolute" />
            <div style="position: absolute; height: 0px; overflow: hidden">
              Username <input tabindex="-1" type="text" name="fake_safari_username" /> Password
              <input tabindex="-1" type="password" name="fake_safari_password" />
            </div>
            <!-- F i x  over -->
            <div>
              <v-text-field
                :label="$t('auth.first_name')"
                :rules="[rules.required, rules.min]"
                :placeholder="$t('auth.first_name')"
                v-model="form.firstName"
                validate-on-blur
                persistent-placeholder
                hide-details="auto"
                required>
                <template v-slot:message="{ message }">
                  <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                  {{ message }}
                </template>
              </v-text-field>
            </div>
            <div class="spacing-top--24">
              <v-text-field
                :label="$t('auth.last_name')"
                :rules="[rules.required, rules.min]"
                :placeholder="$t('auth.last_name')"
                v-model="form.lastName"
                validate-on-blur
                persistent-placeholder
                hide-details="auto"
                required>
                <template v-slot:message="{ message }">
                  <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                  {{ message }}
                </template>
              </v-text-field>
            </div>
            <div class="spacing-top--24">
              <v-text-field
                :rules="[rules.required, rules.email]"
                :label="$t('auth.email')"
                :placeholder="$t('auth.email')"
                @keyup.enter="validateEmailMethod()"
                type="email"
                disabled
                v-model="form.email"
                validate-on-blur
                persistent-placeholder
                hide-details="auto"
                required>
                <template v-slot:message="{ message }">
                  <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                  {{ message }}
                </template>
              </v-text-field>
            </div>
            <!--  
            Prevents the auto-filling of phone number input, also 
            suggests correct username to be saved by the "save password?" prompt 
            (firefox suggests correct username, chrome includes the value of the below input as one of the options
            to be saved as a username)
          -->
            <input
              name="username"
              :value="form.email"
              style="opacity: 0; width: 1px; height: 1px; position: absolute"
              tabindex="-1" />

            <PasswordAndPasswordRepeatBlock v-model="form.password" />

            <v-checkbox
              v-model="agreeToMarketingOffers"
              hide-details
              class="spacing-top--24 join-terms__checkbox"
              color="primary">
              <template v-slot:label>
                <div class="join-terms stepper-text">
                  {{ $t("auth.invite_agreement") }}
                </div>
              </template>
            </v-checkbox>
            <v-btn color="primary" :loading="loading" @click="submit()" x-large class="auth-btn">
              {{ $t("auth.sign_up") }}
            </v-btn>
          </v-form>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
  import { mdiAlertCircle } from "@mdi/js";
  import cleave from "@/directives/cleave";
  import themeConfig from "@themeConfig";
  import { validateEmail } from "@/assets/utilities/helpers/email";
  import { mapActions } from "vuex";
  import { checkForUnsafeChars } from "@/assets/utilities/helpers/sanitization";
  import PasswordAndPasswordRepeatBlock from "@/components/custom/PasswordAndPasswordRepeatBlock.vue";
  import { getMainRole } from "@/assets/utilities/helpers/roles";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    components: {
      PasswordAndPasswordRepeatBlock,
    },
    directives: {
      cleave,
    },
    data() {
      return {
        validateEmail,
        icons: {
          mdiAlertCircle: mdiAlertCircle,
        },
        appDomain: themeConfig.app.domain,
        form: {
          email: "",
          firstName: "",
          lastName: "",
          password: "",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Vilnius",
        },
        hidePassword: true,
        hidePasswordRepeat: true,
        loading: false,
        showForm: false,
        agreeToMarketingOffers: false,
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
          min: value => value.length >= 3 || this.$t("generic.validation.minimum_3_characters"),
          email: value => !!validateEmail(value) || this.$t("generic.validation.email"),
        },
      };
    },
    async beforeCreate() {
      if (checkForUnsafeChars(this.$route.params.token)) {
        try {
          const response = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `user_organizations/invite/${this.$route.params.token}`,
          );
          if (response.data.user.registered) {
            await this.$admin.dataProvider.customHttpCall(
              "POST",
              `user_organizations/invite/${this.$route.params.token}/confirm/existing`,
            );
            // Refresh is required here to store owner data.
            await this.refreshAndStoreAllUserData();
            await this.$router.push({ name: "dashboard" });
          } else {
            this.showForm = true;
            this.form.email = response.data.user.email;
          }
        } catch (e) {
          console.error(e);
          await this.$router.push({ name: "login" });
        }
      } else {
        console.error(this.$t("generic.special_characters_not_allowed"));
      }
    },
    methods: {
      ...mapActions({
        refreshAndStoreAllUserData: "refreshAndStoreAllUserData",
      }),
      validateEmailMethod() {
        this.$refs.initialEmailForm.validate();
      },
      async submit() {
        if (this.$refs.form.validate()) {
          this.loading = true;
          try {
            let response;

            response = await this.$admin.dataProvider.customHttpCall(
              "POST",
              `user_organizations/invite/${this.$route.params.token}/confirm/new`,
              {
                firstName: this.form.firstName,
                lastName: this.form.lastName,
                email: this.form.email,
                password: this.form.password,
                timezone: this.form.timezone,
              },
            );
            if (response) {
              const { user } = response.data;

              const mainRole = getMainRole(user?.organizations[0]?.organizationPermissions);

              let role;
              if (mainRole?.name === "ROLE_EDITOR") {
                role = "Editor";
              }

              if (mainRole?.name === "ROLE_ADMIN") {
                role = "Admin";
              }

              if (this.agreeToMarketingOffers) {
                this.drip.push([
                  "identify",
                  {
                    email: user.email,
                    Role: role,
                  },
                ]);
              } else {
                this.drip.push([
                  "identify",
                  {
                    email: user.email,
                    Role: role,
                    tags: ["Unsubscribed"],
                  },
                ]);
              }
              this.$store.dispatch("SET_USER_DATA", user);
              await this.$router.push({ name: "dashboard" });
            }
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
            console.error(e);
            this.loading = false;
          } finally {
            this.loading = false;
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .join-terms__checkbox {
    &::v-deep {
      .v-input__slot {
        align-items: flex-start;
      }
    }
  }
  .join-terms {
    @include text-caption();
    color: get-color("body");
  }

  .auth-wrapper {
    width: 100%;
    @media (min-width: 600px) {
      margin-top: 32px;
    }
    a {
      text-decoration: unset;
    }
  }

  .auth-container {
    display: flex;
    justify-content: center;
    padding: 4px;
    @media (min-width: 600px) {
      padding: 0;
    }
    .auth-content-container {
      width: 100%;
      @media (min-width: 600px) {
        width: 398px;
      }
    }
  }

  .auth-card {
    padding: 24px;
    border-radius: 6px;
    @media (min-width: 600px) {
      padding: 32px;
    }

    .auth-title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 32px;
    }
    .auth-btn {
      @include text-button-medium();
      background-color: get-color("primary");
      width: 100%;
      color: white;
      padding: 12px 54px;
      border-radius: 4px;
      margin-top: 12px;
      &:hover {
        background-color: get-color("primary-hover");
      }
    }
  }
</style>
